import React, { useEffect, useRef, useState } from 'react';
import { ChevronRight } from 'lucide-react';

const services = [
  {
    title: 'Implementation',
    description: 'Streamline every phase of Infor implementation precisely and on time.',
    image: 'https://ik.imagekit.io/7wqj9br0b/PTKCS/implementation.webp',
  },
  {
    title: 'Managed Services',
    description: 'Manage every complex, time-consuming and resource-intensive process with on-demand maintenance of your Infor applications.',
    image: 'https://ik.imagekit.io/7wqj9br0b/PTKCS/managedservices.webp',
  },
  {
    title: 'Migrate to Infor CloudSuite',
    description: 'Experience seamless and transformative migration from different ERP systems to Infor CloudSuite with data integrity.',
    image: 'https://ik.imagekit.io/7wqj9br0b/PTKCS/migration.webp',
  },
  {
    title: 'Upgrade with Infor',
    description: 'Get access to the latest Infor platform, ensuring improved overall performance.',
    image: 'https://ik.imagekit.io/7wqj9br0b/PTKCS/upgrade.webp',
  },
  {
    title: 'Customization & Integrations',
    description: 'Value-adding custom developments and integrations that deliver better business outcomes.',
    image: 'https://ik.imagekit.io/7wqj9br0b/PTKCS/customization.webp',
  },
];

export default function ServiceOfferings() {
  const trackRef = useRef(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [pageCount, setPageCount] = useState(3);

  useEffect(() => {
    const track = trackRef.current;
    const updatePagination = () => {
      const cards = track.children;
      const step = cards[1].offsetLeft - cards[0].offsetLeft;
      const lastSlide = Math.round((track.scrollWidth - track.clientWidth) / step);
      setPageCount(lastSlide + 1);
      setActiveSlide(Math.min(lastSlide, Math.max(0, Math.round(track.scrollLeft / step))));
    };

    updatePagination();
    const observer = new ResizeObserver(updatePagination);
    observer.observe(track);
    track.addEventListener('scroll', updatePagination, { passive: true });
    return () => {
      observer.disconnect();
      track.removeEventListener('scroll', updatePagination);
    };
  }, []);

  const goToSlide = (index) => {
    const track = trackRef.current;
    const step = track.children[1].offsetLeft - track.children[0].offsetLeft;
    track.scrollTo({
      left: step * index,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  };

  return (
    <section
      className="px-4 py-12 sm:px-6 sm:py-16 lg:px-10 lg:pb-16 xl:px-16"
      style={{ backgroundColor: '#F8FAFC', color: '#1E293B' }}
      aria-labelledby="infor-service-offerings-heading"
      aria-roledescription="carousel"
    >
      <div className="mx-auto max-w-[1740px]">
        <div className="mb-10 grid grid-cols-1 gap-5 text-center md:grid-cols-2 md:gap-10 md:text-left lg:mb-24">
          <h2
            id="infor-service-offerings-heading"
            className="text-2xl font-medium leading-tight tracking-tight sm:text-3xl xl:text-4xl"
          >
            Service Offerings
          </h2>
          <p className="max-w-3xl self-center text-sm font-medium leading-relaxed sm:text-base">
            Our expert Infor service offerings encompass everything from implementation and migration to customizations tailored for all businesses.
          </p>
        </div>

        <div
          id="infor-service-offerings-track"
          ref={trackRef}
          className="relative flex snap-x snap-mandatory gap-6 overflow-x-auto rounded-3xl [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:gap-8"
          tabIndex={0}
          aria-label="Infor services. Scroll to explore all five offerings."
        >
          {services.map((service, index) => (
            <article
              key={service.title}
              className="flex min-w-0 shrink-0 basis-full snap-start flex-col overflow-hidden rounded-3xl md:basis-[calc((100%-24px)/2)] lg:basis-[calc((100%-64px)/3)]"
              style={{ backgroundColor: '#FFFFFF', color: '#1E293B' }}
              aria-label={`${index + 1} of ${services.length}: ${service.title}`}
            >
              <img
                src={service.image}
                alt=""
                loading="lazy"
                decoding="async"
                width="1280"
                height="720"
                className="aspect-video w-full object-cover"
              />
              <div className="flex flex-1 flex-col p-6 xl:p-7">
                <h3 className="text-lg font-medium leading-snug xl:text-xl" style={{ color: '#B45309' }}>
                  {service.title}
                </h3>
                <p className="mb-3 mt-6 text-sm leading-relaxed xl:mt-8 xl:text-base">
                  {service.description}
                </p>
                <a
                  href="#infor-contact"
                  className="mt-auto flex h-9 w-9 items-center justify-center self-end rounded-full motion-safe:transition-transform motion-safe:hover:translate-x-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                  style={{ color: '#B45309', backgroundColor: '#FFFFFF', outlineColor: '#B45309' }}
                  aria-label={`Contact us about ${service.title}`}
                >
                  <ChevronRight className="h-5 w-5" aria-hidden="true" />
                </a>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-3 flex justify-center">
          <div className="flex rounded-full px-2" style={{ backgroundColor: '#FFEDD5' }} aria-label="Service carousel navigation">
            {Array.from({ length: pageCount }, (_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => goToSlide(index)}
                className="flex h-8 w-8 items-center justify-center rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                style={{ outlineColor: '#B45309' }}
                aria-label={`Show services starting with ${services[index].title}`}
                aria-current={activeSlide === index ? 'true' : undefined}
                aria-controls="infor-service-offerings-track"
              >
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: activeSlide === index ? '#F48128' : '#D6BFA8' }} />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
